import type { Client } from '../types/crm';
import { clientById, idsMatch } from './crmHelpers';
import { displayClientDistrict } from './singaporeDistrict';

export interface AccountListEntry {
  client: Client;
  depth: number;
  isSubCompany: boolean;
  parentName?: string;
  hasSubCompanies?: boolean;
  isLastSub?: boolean;
}

function entryMatches(entry: AccountListEntry, query: string): boolean {
  const sp = (entry.client.owner_id ?? '').toLowerCase();
  const district = displayClientDistrict(entry.client).toLowerCase();
  const registered = (entry.client.intel_profile_json?.registeredAddress ?? '').toLowerCase();
  const pool =
    `${entry.client.company_name} ${entry.client.code} ${entry.client.client_type ?? ''} ${entry.parentName ?? ''} ${sp} ${district} ${entry.client.address ?? ''} ${registered}`.toLowerCase();
  return pool.includes(query);
}

function splitAccountGroups(entries: AccountListEntry[]): AccountListEntry[][] {
  const groups: AccountListEntry[][] = [];
  let current: AccountListEntry[] = [];

  for (const entry of entries) {
    if (!entry.isSubCompany) {
      if (current.length > 0) groups.push(current);
      current = [entry];
      continue;
    }
    // Never attach a brand to a missing/previous root (e.g. orphan after salesperson filter).
    if (current.length === 0) {
      current = [{ ...entry, depth: 0, isSubCompany: false, hasSubCompanies: false }];
      continue;
    }
    current.push(entry);
  }

  if (current.length > 0) groups.push(current);
  return groups;
}

export type AccountSortKey = 'type' | 'account' | 'district' | 'salesperson';
export type AccountSortDirection = 'asc' | 'desc';

function compareClients(
  a: Client,
  b: Client,
  key: AccountSortKey,
  direction: AccountSortDirection,
): number {
  let cmp = 0;
  if (key === 'type') {
    cmp = (a.client_type ?? '').localeCompare(b.client_type ?? '', undefined, { sensitivity: 'base' });
  } else if (key === 'district') {
    cmp = displayClientDistrict(a).localeCompare(displayClientDistrict(b), undefined, {
      sensitivity: 'base',
    });
  } else if (key === 'salesperson') {
    cmp = (a.owner_id ?? '').localeCompare(b.owner_id ?? '', undefined, { sensitivity: 'base' });
  } else {
    cmp = a.company_name.localeCompare(b.company_name, undefined, { sensitivity: 'base' });
  }
  if (cmp === 0 && key !== 'account') {
    cmp = a.company_name.localeCompare(b.company_name, undefined, { sensitivity: 'base' });
  }
  return direction === 'asc' ? cmp : -cmp;
}

/** Keep sub-companies directly under their parent when sorting. */
export function sortAccountListEntries(
  entries: AccountListEntry[],
  key: AccountSortKey = 'account',
  direction: AccountSortDirection = 'asc',
): AccountListEntry[] {
  const groups = splitAccountGroups(entries);
  groups.sort((a, b) => compareClients(a[0].client, b[0].client, key, direction));

  const sorted: AccountListEntry[] = [];
  for (const group of groups) {
    const [root, ...subs] = group;
    const orderedSubs = [...subs].sort((a, b) =>
      a.client.company_name.localeCompare(b.client.company_name),
    );
    sorted.push({ ...root, hasSubCompanies: orderedSubs.length > 0 });
    orderedSubs.forEach((sub, index) => {
      sorted.push({ ...sub, isLastSub: index === orderedSubs.length - 1 });
    });
  }
  return sorted;
}

/** Client ids included when viewing an account (parent + its sub-companies). */
export function clientIdsInAccountScope(clients: Client[], client: Client): string[] {
  const ids = [String(client.id)];
  if (isRootAccount(client)) {
    for (const sub of subCompaniesForParent(clients, client.id)) {
      ids.push(String(sub.id));
    }
  }
  return ids;
}

export function contactSourceAccountName(
  clients: Client[],
  contact: { client_id: string },
  viewingClient: Client,
): string | undefined {
  if (idsMatch(contact.client_id, viewingClient.id)) return undefined;
  return clientById(clients, contact.client_id)?.company_name;
}

/** Direct child brands / sub-companies of a parent account. */
export function subCompaniesForParent(clients: Client[], parentId: string): Client[] {
  return clients
    .filter((c) => idsMatch(c.parent_client_id, parentId))
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
}

export function isSubCompany(client: Client | undefined): boolean {
  return Boolean(client?.parent_client_id);
}

export function isRootAccount(client: Client | undefined): boolean {
  return Boolean(client && !client.parent_client_id);
}

export function parentAccount(clients: Client[], client: Client | undefined): Client | undefined {
  if (!client?.parent_client_id) return undefined;
  return clientById(clients, client.parent_client_id);
}

/** Roots first, then each parent's sub-companies indented (one level). */
export function buildAccountListEntries(clients: Client[]): AccountListEntry[] {
  const roots = clients
    .filter((c) => !c.parent_client_id)
    .sort((a, b) => a.company_name.localeCompare(b.company_name));

  const entries: AccountListEntry[] = [];
  const seen = new Set<string>();

  for (const root of roots) {
    const subs = subCompaniesForParent(clients, root.id);
    entries.push({ client: root, depth: 0, isSubCompany: false, hasSubCompanies: subs.length > 0 });
    seen.add(String(root.id));

    subs.forEach((sub, index) => {
      entries.push({
        client: sub,
        depth: 1,
        isSubCompany: true,
        parentName: root.company_name,
        isLastSub: index === subs.length - 1,
      });
      seen.add(String(sub.id));
    });
  }

  // Brands whose parent is outside the filtered set (e.g. Neo Group not under SA5)
  // must stay top-level — never nest under the last unrelated root (e.g. Zhong Yi…).
  for (const orphan of clients) {
    if (seen.has(String(orphan.id))) continue;
    const parent = parentAccount(clients, orphan);
    entries.push({
      client: orphan,
      depth: 0,
      isSubCompany: false,
      hasSubCompanies: false,
      // Hint only when parent happens to still be resolvable in this list.
      parentName: parent?.company_name,
    });
    seen.add(String(orphan.id));
  }

  return entries;
}

export function filterAccountListEntries(
  entries: AccountListEntry[],
  query: string,
): AccountListEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;

  const groups = splitAccountGroups(entries);

  return groups.flatMap((group) => {
    const [root, ...subs] = group;
    const rootMatches = entryMatches(root, q);
    const matchingSubs = subs.filter((sub) => entryMatches(sub, q));

    if (rootMatches) {
      return [{ ...root, hasSubCompanies: subs.length > 0 }, ...subs];
    }
    if (matchingSubs.length > 0) {
      return [{ ...root, hasSubCompanies: true }, ...matchingSubs];
    }
    return [];
  });
}
